import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  BookOpen, ArrowLeft, Loader2, AlertTriangle, Shield, Calculator,
  ChevronRight, Filter
} from "lucide-react";

const SECTOR_LABELS = {
  medical_device: "Medical Device", automotive: "Automotive", aerospace: "Aerospace",
  electronics: "Electronics", quality: "Quality", laboratory: "Laboratory",
};

const DIFFICULTY_COLORS = {
  beginner: "text-emerald-400 bg-emerald-900/20 border-emerald-800/30",
  intermediate: "text-amber-400 bg-amber-900/20 border-amber-800/30",
  advanced: "text-red-400 bg-red-900/20 border-red-800/30",
};

const CaseStudiesPage = () => {
  const { t } = useTranslation('modules');
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [sectorFilter, setSectorFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/eguide/cases`, { params: sectorFilter ? { sector: sectorFilter } : {} })
      .then(res => { setCases(res.data.cases || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sectorFilter]);

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  /* ─── Case Detail ──────────────────────────────────── */
  if (selectedCase) {
    const c = selectedCase;
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="case-detail">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={() => setSelectedCase(null)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-cases">
            <ArrowLeft className="w-4 h-4" /> All Cases
          </button>
          <span className={`text-[10px] px-2 py-0.5 rounded border ${DIFFICULTY_COLORS[c.difficulty]}`}>{c.difficulty}</span>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="mb-6">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-widest font-bold mb-2">{SECTOR_LABELS[c.sector] || c.sector}</div>
            <h1 className="text-2xl font-bold mb-2" data-testid="case-title">{c.title}</h1>
            <p className="text-sm text-slate-400">{c.failure_mode}</p>
          </div>

          {/* Scenario */}
          <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 mb-4">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">The Scenario</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{c.scenario}</p>
          </div>

          {/* Key Question */}
          <div className="bg-amber-900/10 border border-amber-800/20 rounded-lg p-4 mb-4 flex items-start gap-3" data-testid="key-question">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-bold text-amber-400 mb-1">Key Audit Question</div>
              <p className="text-sm text-amber-200">{c.key_question}</p>
            </div>
          </div>

          {/* Data */}
          {c.data && (
            <div className="bg-[#132340] border border-[#1E3A5F] rounded-lg p-4 mb-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Data</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(c.data).map(([k, v]) => (
                  <div key={k} className="bg-[#0A1628] rounded p-2">
                    <div className="text-[10px] text-slate-500 uppercase">{k.replace(/_/g, " ")}</div>
                    <div className="text-sm font-mono text-white">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolution */}
          <div className="bg-emerald-900/10 border border-emerald-800/20 rounded-lg p-5 mb-4">
            <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-2">Resolution</h3>
            <p className="text-sm text-slate-300 leading-relaxed">{c.resolution}</p>
          </div>

          {/* Defence Script */}
          <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 mb-4">
            <div className="text-[10px] text-[#00C8E0] uppercase tracking-wider mb-2 font-bold">Audit Defence Script</div>
            <p className="text-sm text-slate-300 leading-relaxed italic">"{c.defense_script}"</p>
          </div>

          {/* Standards */}
          <div className="flex flex-wrap gap-2 mb-4">
            {c.standards?.map((s, i) => (
              <span key={i} className="bg-[#132340] border border-[#1E3A5F] rounded px-2 py-1 text-[10px] text-slate-400">{s}</span>
            ))}
          </div>

          {/* Calculator link */}
          {c.calculator_link && (
            <button
              onClick={() => navigate("/calculators")}
              className="flex items-center gap-2 text-sm text-[#00C8E0] hover:text-white transition-colors"
              data-testid="calc-link"
            >
              <Calculator className="w-4 h-4" /> Open {c.calculator_link.toUpperCase()} Calculator <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  /* ─── Case List ──────────────────────────────────── */
  const sectors = [...new Set(cases.map(c => c.sector))];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="cases-page">
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm">Case Study Navigator</h1>
              <p className="text-[10px] text-slate-500">{cases.length} real-world audit case studies</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <select
              value={sectorFilter}
              onChange={(e) => setSectorFilter(e.target.value)}
              className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white focus:outline-none"
              data-testid="sector-filter"
            >
              <option value="">All Sectors</option>
              {sectors.map(s => <option key={s} value={s}>{SECTOR_LABELS[s] || s}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-3">
        {cases.map(c => (
          <button
            key={c.case_id}
            onClick={() => setSelectedCase(c)}
            className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-5 hover:border-[#1A6BCC]/40 transition-colors group"
            data-testid={`case-${c.case_id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] text-[#00C8E0] uppercase tracking-wider font-bold">{SECTOR_LABELS[c.sector] || c.sector}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFFICULTY_COLORS[c.difficulty]}`}>{c.difficulty}</span>
                </div>
                <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors mb-1">{c.title}</h3>
                <p className="text-xs text-slate-400 line-clamp-2">{c.failure_mode}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#00C8E0] shrink-0 mt-2" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CaseStudiesPage;
